import React from 'react';
import { Button, Checkbox, SparkIcon, Spinner, TooltipTrigger } from '@spark';

/** Figma copy for the empty state (3926-8787). */
export const NO_TOOLS_FOUND_TEXT = 'No tools found, Paste tools in tool discovery';

export const CLEAR_ALL_LABEL = 'Clear All';
export const SELECT_ALL_LABEL = 'Select All';

/**
 * "Tools Selected (N)" block of a Tool Discovery card. N counts the checked
 * tools, not every discovered one.
 *
 * - Empty (Figma 4012:16639): `Tools Selected (0)` + hint, 8px apart.
 * - Populated (Figma 4012:16848): `Tools Selected (N):` beside a wrapping grid
 *   of 183px checkbox items (row gap 8, column gap 16), checked = kept. At the
 *   row's end sits one bulk action: "✕ Clear All" while anything is checked,
 *   "Select All" once nothing is (needs `onSelectedChange`).
 * - `loading` shows a spinner while discovery is in flight; `error` renders
 *   the discovery failure inline.
 */
const ToolsFoundList = ({
    tools = [],
    selectedTools = [],
    onToggle,
    onSelectedChange,
    loading = false,
    error = null,
}) => {
    const selected = new Set(selectedTools);
    const hasTools = !loading && tools.length > 0;
    const selectedCount = tools.filter((tool) => selected.has(tool.name)).length;
    return (
        <div className="cw-ac-tools-found" data-testid="tools-found">
            <div className={`cw-ac-tools-found__row${hasTools ? ' cw-ac-tools-found__row--grid' : ''}`}>
                <span className="cw-ac-tools-found__title">
                    {hasTools ? `Tools Selected (${selectedCount}):` : `Tools Selected (${selectedCount})`}
                </span>
                {loading && (
                    <span className="cw-ac-tools-found__loading">
                        <Spinner size="small" />
                        <span>Discovering tools…</span>
                    </span>
                )}
                {!loading && !tools.length && (
                    <span className="cw-ac-tools-found__empty">{NO_TOOLS_FOUND_TEXT}</span>
                )}
                {hasTools && (
                    <div className="cw-ac-tools-found__grid" role="group" aria-label="Tools selected">
                        {tools.map((tool) => {
                            const checkbox = (
                                <Checkbox
                                    label={tool.name}
                                    status={selected.has(tool.name) ? 'active' : 'inactive'}
                                    onChange={() => onToggle?.(tool.name)}
                                    className="cw-ac-tools-found__item"
                                />
                            );
                            return tool.description ? (
                                <TooltipTrigger key={tool.name} text={tool.description}>
                                    <span className="cw-ac-tools-found__item-wrap">{checkbox}</span>
                                </TooltipTrigger>
                            ) : (
                                <span key={tool.name} className="cw-ac-tools-found__item-wrap">
                                    {checkbox}
                                </span>
                            );
                        })}
                    </div>
                )}
                {hasTools && onSelectedChange && (
                    <div className="cw-ac-tools-found__actions">
                        {selectedCount > 0 ? (
                            <Button
                                size="tiny"
                                variant="ghost"
                                startIcon={<SparkIcon slug="close" size="small" />}
                                onClick={() => onSelectedChange([])}
                            >
                                {CLEAR_ALL_LABEL}
                            </Button>
                        ) : (
                            <Button
                                size="tiny"
                                variant="ghost"
                                startIcon={<SparkIcon slug="check" size="small" />}
                                onClick={() => onSelectedChange(tools.map((tool) => tool.name))}
                            >
                                {SELECT_ALL_LABEL}
                            </Button>
                        )}
                    </div>
                )}
            </div>
            {error && <div className="cw-field-error cw-ac-tools-found__error">{error}</div>}
        </div>
    );
};

export default ToolsFoundList;
